import smoothscroll from "smoothscroll-polyfill";
import { watchScrollDirection } from "./utils";

smoothscroll.polyfill();
watchScrollDirection();

/**
 * Component: Anchor
 *
 * An invisible anchor on the page that can be linked to via anchor links.
 *
 * @param {Object} props
 * @param {String} props.id    Anchor-linkable id attr.
 * @param {String} props.text  Human-readable text for use in the link pointing to this anchor.
 *
 * @return {Element}
 */
function Anchor({ id, text }) {
  const anchor = document.createElement("a");

  anchor.id = id;
  anchor.className = "anchor-link anchor-link--flush";
  anchor.dataset.text = text;
  anchor.setAttribute("aria-hidden", "true");

  let headerHeight = document.querySelector(".site-header").offsetHeight;
  if (document.body.classList.contains("single-article"))
    headerHeight = headerHeight + 64;
  anchor.style.top = `-${headerHeight}px`;

  return anchor;
}

/**
 * Component: AnchorNav
 *
 * A sticky nav comprised of dynamic anchor links to sections on the page.
 *'
 * @param {Object}    props
 * @param {NodeList}  props.anchors    The set of anchors to link to. Can be empty.
 * @param {Number}   [props.offset=45] Set a "padding" from the site header for start position.
 * @param {Number}   [props.stickyOffset=8] Set a "padding" from the site header for stuck position.
 * @param {Bool}     [props.alphabet=false] Flag used for adding inactive letters to a-z navs
 *
 * @return {Element}  The anchor nav DOM element.
 */
export function AnchorNav({
  anchors,
  offset = 45,
  stickyOffset = 8,
  alphabet = false,
}) {
  /**
   * @typedef  {?('natural'|'sticky'|'bottomed')} Mode
   */

  /**
   * @type {Mode}
   */
  let mode = null;
  let anchorClicked = null;
  const wrapper = document.createElement("nav");

  /**
   * Component Method: findActiveAnchor
   *
   * Finds the first anchor link "visible" on the page when scrolling down,
   * finds the anchor just before the first anchor off the bottom of the screen
   * when scrolling up, or just returns the last child when "bottomed out"
   *
   * @return {String} The anchor's id attr.
   */
  const findActiveAnchor = () => {
    let activeAnchor;

    const scrollDirection = document.body.dataset.scrollDirection || "DOWN";

    if (scrollDirection === "DOWN") {
      activeAnchor = Array.from(anchors).find((anchor) => {
        const bounds = anchor.parentElement.getBoundingClientRect();
        return bounds.top >= 0 && bounds.bottom <= window.innerHeight;
      });
    }

    if (scrollDirection === "UP") {
      const firstOffscreenAnchorIndex = Array.from(anchors).findIndex(
        (anchor) => {
          const bounds = anchor.parentElement.getBoundingClientRect();
          return bounds.bottom >= window.innerHeight;
        }
      );
      if (firstOffscreenAnchorIndex > 0)
        activeAnchor = anchors[firstOffscreenAnchorIndex - 1];
    }

    if (activeAnchor) return activeAnchor.id;
  };

  /**
   * Component Method: setActiveAnchor
   *
   * Sets the active anchor by adding/removing 'active' class across the anchor links.
   *
   * @param {String=} activeId The id to make active
   */
  const setActiveAnchor = (activeId = null) => {
    if (!activeId && anchorClicked) return;
    if (!activeId) activeId = findActiveAnchor();
    if (activeId) {
      wrapper.querySelector(".active").classList.remove("active");
      wrapper.querySelector(`[href="#${activeId}"]`).classList.add("active");
    }
  };

  /**
   * Component Method: handleClick
   *
   * Performs a smooth scroll to the anchor and sets active state in the nav
   *
   * @param {Event} e A click event on the nav.
   */
  const handleClick = (e) => {
    e.preventDefault();

    if (e.target.closest("a")) {
      anchorClicked = true;
      const targetId = e.target.closest("a").href.split("#")[1];

      setActiveAnchor(targetId);

      document.getElementById(targetId).scrollIntoView({
        block: "start",
        inline: "nearest",
        behavior: "smooth",
      });

      setTimeout(() => {
        anchorClicked = false;
      }, 1000);
    }
  };

  /**
   * Component Method: getModeByPosition
   *
   * Check if the anchor nav should be sticky based on its parent scroll position
   *
   * @returns {Mode} The desired display mode.
   */
  const getModeByPosition = () => {
    const scrolledToStickiness =
      wrapper.parentElement.getBoundingClientRect().top +
        offset -
        stickyOffset <=
      document.querySelector(".site-header").offsetHeight;

    const scrolledBeyondStickiness =
      wrapper.parentElement.getBoundingClientRect().bottom <=
      document.querySelector(".site-header").offsetHeight +
        offset +
        45 + //? bottom offset
        wrapper.offsetHeight;

    if (scrolledBeyondStickiness) return "bottomed";

    if (scrolledToStickiness && !scrolledBeyondStickiness) return "sticky";

    return "natural";
  };

  /**
   * Component Method: makeSticky
   *
   * Makes the anchor nav sticky. Doesn't re-run if already sticky.
   */
  const makeSticky = () => {
    if (mode === "sticky") return;

    const headerHeight = document.querySelector(".site-header").offsetHeight;

    wrapper.style.top = headerHeight + stickyOffset + "px";
    wrapper.style.bottom = "auto";
    wrapper.style.position = "fixed";
    mode = "sticky";

    // special treatment for articles: margin-based offset (this is transitionable)
    if (document.body.classList.contains("single-article"))
      wrapper.style.marginTop = "64px";
  };

  /**
   * Component Method: makeNatural
   *
   * Returns the anchor nav to natural scrolling. Doesn't re-run if already natural.
   */
  const makeNatural = () => {
    if (mode === "natural") return;

    wrapper.style.top = offset + "px";
    wrapper.style.bottom = "auto";
    wrapper.style.position = "absolute";
    wrapper.style.marginTop = 0;
    mode = "natural";
  };

  /**
   * Component Method: makeBottomed
   *
   * Sets the anchor nav in the bottom of its parent with natural scrolling. Doesn't re-run if already bottomed.
   */
  const makeBottomed = () => {
    if (mode === "bottomed") return;

    wrapper.style.top = "auto";
    wrapper.style.bottom = offset + "px";
    wrapper.style.position = "absolute";
    wrapper.style.marginTop = 0;
    mode = "bottomed";
  };

  /**
   * Component Method: modeSwitch
   *
   * Helper to call the desired mode funciton.
   *
   * @param {Mode} newMode The desired mode.
   */
  const modeSwitch = (newMode) => {
    switch (newMode) {
      case "natural":
        makeNatural();
        break;
      case "sticky":
        makeSticky();
        break;
      case "bottomed":
        makeBottomed();
        break;
    }
  };

  // Component template
  wrapper.className = "anchor-nav";
  const initialActiveAnchor =
    findActiveAnchor() || anchors.length ? anchors[0].id : null;

  // ? cast anchors to array
  // ? this allows us to splice in inactive anchors, and map over them
  // ? but we still run our other component methods against the original NodeList param
  let displayAnchors = Array.from(anchors);

  // * Maybe Inject "inactive" anchor links
  if (alphabet) {
    "abcdefghijklmnopqrstuvwxyz".split("").forEach((letter, i) => {
      if (!displayAnchors.some((anchor) => anchor.id === letter)) {
        displayAnchors.splice(i, 0, letter);
      }
    });
  }

  wrapper.innerHTML = displayAnchors
    .map((anchor) => {
      if (typeof anchor === "string")
        return `<a class="anchor-nav-link inactive">${anchor.toUpperCase()}</a>`;
      else
        return `<a href="#${anchor.id}" class="anchor-nav-link ${
          anchor.id === initialActiveAnchor ? "active" : ""
        }">${anchor.dataset.text}</a>`;
    })
    .join("\n");

  // Mobile Version
  if (
    window.matchMedia("(max-width: 960px)").matches ||
    (window.matchMedia("(max-height: 767px)").matches && alphabet === true)
  ) {
    return MobileAnchorNav({ initialActiveAnchor, findActiveAnchor, anchors });
  }

  // Component events

  //* on render
  requestAnimationFrame(() => modeSwitch(getModeByPosition()));

  //* on scroll
  document.addEventListener("scroll", function handler() {
    // ? if the nav has been removed from the dom, garbage collect this listener
    if (!document.body.contains(wrapper)) {
      this.removeEventListener("scroll", handler);
      return;
    }

    setActiveAnchor();
    modeSwitch(getModeByPosition());
  });

  //* on click
  wrapper.addEventListener("click", handleClick);

  return wrapper;
}

/**
 * Component: MobileAnchorNav
 *
 * The mobile version the dynamic anchor nav.
 *
 * @param {Object}    props
 * @param {String}    props.initialActiveAnchor  The id of the initial active anchor.
 * @param {Function}  props.findActiveAnchor     A function for finding the active anchor based on scroll position.
 * @param {NodeList}     props.anchors              The set of anchors to link to. Can be empty.
 *
 * @return {Element}  The anchor nav DOM element.
 */
function MobileAnchorNav({ initialActiveAnchor, findActiveAnchor, anchors }) {
  const wrapper = document.createElement("nav");

  /**
   * Component Method: handleChange
   *
   * Performs a smooth scroll to the anchor and sets the nav value.
   *
   * @param {Event} e A click event on the nav.
   */
  const handleChange = (e) => {
    const targetId = e.target.value.split("#")[1];

    // TODO
    setActiveAnchor(targetId);

    document.getElementById(targetId).scrollIntoView({
      block: "start",
      inline: "nearest",
      behavior: "smooth",
    });
  };

  /**
   * Component Method: setActiveAnchor
   *
   * Matches the <select> field value with the active anchor on page.
   */
  const setActiveAnchor = () => {
    const activeId = findActiveAnchor();
    if (activeId) {
      wrapper.querySelector("select").value = `#${activeId}`;
    }
  };

  // Component template
  wrapper.className = "mobile-anchor-nav";
  wrapper.style.top =
    document.querySelector(".site-header").offsetHeight + "px";
  wrapper.innerHTML = `
    <select>
      ${Array.from(anchors)
        .map(
          (anchor) =>
            `<option value="#${anchor.id}" ${
              anchor.id === initialActiveAnchor ? "active" : ""
            }">${anchor.dataset.text}</option>`
        )
        .join("\n")}
    </select>
  `;

  // Component events
  //* on scroll
  document.addEventListener("scroll", function handler() {
    // ? if the nav has been removed from the dom, garbage collect this listener
    if (!document.body.contains(wrapper)) {
      this.removeEventListener("scroll", handler);
      return;
    }

    setActiveAnchor();
  });
  //* on change
  wrapper.querySelector("select").addEventListener("change", handleChange);

  return wrapper;
}

/**
 * Function: Generate anchors on page for sticky anchor nav to link to.
 *
 * @param {Object}  options
 * @param {'alphabet'|'article'|'block'|'exhibition'}  options.mode  Determines how to parse the DOM for linkable "sections"
 *
 * @return {NodeList} The set of DOM nodes added. Can be empty.
 */
export function injectAnchors({ mode }) {
  let targets = [];
  switch (mode) {
    case "alphabet":
      targets = "abcdefghijklmnopqrstuvwxyz"
        .split("")
        .map((letter) => document.querySelector(`[data-letter="${letter}"]`))
        .filter((target) => !!target);
      break;

    case "article":
      targets = document.querySelectorAll(
        ".article-content h2, .article-content h3"
      );
      break;

    case "block":
      targets = document.querySelectorAll("[data-block-anchored]");
      break;

    case "exhibition":
      targets = document.querySelectorAll("h3.chapter-title");
      break;
  }

  if (targets.length) {
    targets.forEach((target) => {
      target.style.position = "relative";
      if (mode === "alphabet") {
        const letter = target.dataset.letter;
        target.prepend(Anchor({ id: letter, text: letter.toUpperCase() }));
      } else {
        // Clean anchor text removing non-alphanumeric characers and spaces.
        const id = target.textContent
          .toLowerCase()
          .replace(/[^a-z0-9 -]/g, "")
          .replace(/\s+/g, "-");
        const text =
          mode === "block" && target.dataset.anchorOverride
            ? target.dataset.anchorOverride
            : target.textContent;
        // put the anchor in the "chapter numeral" for exhibitions when possible
        if (mode === "exhibition" && target.previousElementSibling) {
          target = target.previousElementSibling;
        }
        target.prepend(Anchor({ id, text }));
      }
    });
  }

  return document.querySelectorAll(".anchor-link--flush");
}

/**
 * Function: killAnchorNav
 *
 * Removes any anchor nav, mobile anchor nav, and all dynamic anchors from a page.
 */
export function killAnchorNav() {
  const nav = document.querySelector(".anchor-nav, .mobile-anchor-nav");
  if (!nav) return;

  nav.remove();
  document
    .querySelectorAll(".feed-teasers .anchor-link")
    .forEach((anchor) => anchor.remove());
}

// Initialize on load

// * Article
if (
  document.body.classList.contains("single-article") &&
  document.querySelectorAll(".article-content h2, .article-content h3").length
) {
  document
    .querySelector(".article-wrap")
    .prepend(AnchorNav({ anchors: injectAnchors({ mode: "article" }) }));
}

// * Exhibition
if (
  document.body.classList.contains("single-exhibition") &&
  document.querySelectorAll("h3.chapter-title").length
)
  document
    .querySelector(".exhibition-wrap")
    .prepend(AnchorNav({ anchors: injectAnchors({ mode: "exhibition" }) }));

// * Author Index
if (document.querySelector(".authorindex"))
  document.querySelector(".block-post-index").prepend(
    AnchorNav({
      anchors: injectAnchors({ mode: "alphabet" }),
      alphabet: true,
    })
  );

// * Article Index
if (document.querySelector(".page-slug-articles"))
  document.querySelector(".block-post-index").prepend(
    AnchorNav({
      anchors: injectAnchors({ mode: "alphabet" }),
      alphabet: true,
      offset: 120,
    })
  );

// * Individual Pages
if (document.querySelector("[data-block-anchored]")) {
  // When processing custom block pages, its useful to separate out the page header
  // where applicable, and wrap the rest of the blocks.
  // ? This is because page header _is_ a block, but we want it to behave like its not
  // ? The implementation of page header does not get the "<section.block>"
  // ? treatment like the rest of blocks, so the following will produce a header
  // ? followed by a single div containing all the rest of the blocks.
  // ? That div gets position relative and serves as the parent context for the anchor nav
  const wrapper = document.querySelector(".type-page"),
    blocks = wrapper.querySelectorAll("section.block"),
    newBlockWrapper = document.createElement("div");
  newBlockWrapper.className = "block-wrapper";
  newBlockWrapper.style.position = "relative";
  blocks.forEach((block) => newBlockWrapper.appendChild(block));
  wrapper.appendChild(newBlockWrapper);

  document.querySelector(".block-wrapper").prepend(
    AnchorNav({
      anchors: injectAnchors({ mode: "block" }),
    })
  );
}
